import React, { useState, useEffect, useRef, createContext, useContext, ReactNode, useCallback } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import WarningModal from './../v2BaseComponents/AutoLogoutWarningModal';
import { useSignOutAndResetApp } from '../../hooks/useSignOutAndResetApp';

const WARNING_PERIOD_FOR_TIMEOUT_MORE_THAN_ONE_MIN = 60;
const WARNING_PERIOD_FOR_TIMEOUT_LESS_THAN_ONE_MIN = 30;

interface SessionContextType {
    inactivityTimerValue: number;
    isInWarningPeriod: boolean;
    triggerActivity: () => void;
    useSetTimeout: (minutes: number, dependencies?: React.DependencyList) => void;
    setSessionTimeoutMinutes: (minutes: number) => void;
}

interface SessionProviderProps {
    children: ReactNode;
}

// Context for managing session timeout globally
const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
    const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number>(0); // 0 = disabled
    const [inactivityTimerValue, setInactivityTimerValue] = useState<number>(0);
    const [isInWarningPeriod, setIsInWarningPeriod] = useState<boolean>(false);
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
    const [currentWarningPeriod, setCurrentWarningPeriod] = useState<number>(
        WARNING_PERIOD_FOR_TIMEOUT_MORE_THAN_ONE_MIN,
    ); // Store current warning period

    const { signOutAndResetApp } = useSignOutAndResetApp();

    // Single timer reference
    const timer = useRef<NodeJS.Timeout | null>(null);

    // Calculate warning period based on session timeout
    const calculateWarningPeriod = (timeoutMinutes: number): number => {
        if (timeoutMinutes <= 1) {
            return WARNING_PERIOD_FOR_TIMEOUT_LESS_THAN_ONE_MIN; // 30 seconds for 1 minute or less
        }
        return WARNING_PERIOD_FOR_TIMEOUT_MORE_THAN_ONE_MIN; // 60 seconds for anything longer
    };

    const handleLogout = useCallback((): void => {
        clearTimer();
        signOutAndResetApp();
    }, [signOutAndResetApp]);

    const clearTimer = (): void => {
        if (timer.current) {
            clearInterval(timer.current);
            timer.current = null;
        }
    };

    const startTimer = (): void => {
        if (sessionTimeoutMinutes <= 0) return;

        clearTimer();

        const totalSeconds = sessionTimeoutMinutes * 60;
        const warningPeriodSeconds = calculateWarningPeriod(sessionTimeoutMinutes);

        // Store the warning period for the modal
        setCurrentWarningPeriod(warningPeriodSeconds);

        setInactivityTimerValue(totalSeconds);
        setIsInWarningPeriod(false);
        setShowWarningModal(false);

        // Single timer that handles everything
        timer.current = setInterval(() => {
            setInactivityTimerValue((prev) => {
                const newValue = prev - 1;

                // Enter warning period based on calculated warning time
                if (newValue === warningPeriodSeconds) {
                    setIsInWarningPeriod(true);
                    setShowWarningModal(true);
                }

                // Logout at 0 seconds
                if (newValue <= 0) {
                    handleLogout();
                    return 0;
                }

                return newValue;
            });
        }, 1000);
    };

    const triggerActivity = (): void => {
        // Don't reset during warning period
        if (isInWarningPeriod) {
            return;
        }

        startTimer();
    };

    const continueSession = (): void => {
        setIsInWarningPeriod(false);
        setShowWarningModal(false);
        startTimer(); // Restart the full timer
    };

    // Hook that handles setting timeout with custom dependencies
    const useSetTimeout = (minutes: number, dependencies: React.DependencyList = []): void => {
        useEffect(() => {
            setSessionTimeoutMinutes(minutes);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [minutes, ...dependencies]);
    };

    // Start/stop timer when sessionTimeoutMinutes changes
    useEffect(() => {
        if (sessionTimeoutMinutes > 0) {
            startTimer();
        } else {
            clearTimer();
            setInactivityTimerValue(0);
            setIsInWarningPeriod(false);
            setShowWarningModal(false);
            setCurrentWarningPeriod(WARNING_PERIOD_FOR_TIMEOUT_MORE_THAN_ONE_MIN); // Reset to default
        }

        return clearTimer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionTimeoutMinutes]);

    // Cleanup on unmount
    useEffect(() => {
        return clearTimer;
    }, []);

    return (
        <SessionContext.Provider
            value={{
                inactivityTimerValue,
                isInWarningPeriod,
                triggerActivity,
                useSetTimeout,
                setSessionTimeoutMinutes,
            }}
        >
            {children}
            <WarningModal
                visible={showWarningModal}
                onContinue={continueSession}
                onTimeout={handleLogout}
                warningPeriodSeconds={currentWarningPeriod}
            />
        </SessionContext.Provider>
    );
};

export const useSession = (): SessionContextType => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};

// Simple hook for components that need timer info
export const useAutoLogout = () => {
    const { inactivityTimerValue, isInWarningPeriod, triggerActivity, useSetTimeout } = useSession();
    return {
        inactivityTimerValue,
        isInWarningPeriod,
        triggerActivity,
        useSetTimeout,
    };
};

// Activity tracker component
export const ActivityTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { triggerActivity } = useSession();

    // Set up global activity listeners for web
    useEffect(() => {
        if (Platform.OS === 'web') {
            const events = ['mousedown', 'keypress', 'click', 'touchstart', 'scroll'];

            events.forEach((event) => {
                window.addEventListener(event, triggerActivity, { passive: true, capture: true });
            });

            return () => {
                events.forEach((event) => {
                    window.removeEventListener(event, triggerActivity, { capture: true });
                });
            };
        }
    }, [triggerActivity]);

    return (
        <View style={styles.activityContainer} onTouchStart={triggerActivity}>
            {children}
        </View>
    );
};

const styles = StyleSheet.create({
    activityContainer: {
        flex: 1,
    },
});
